/**
 * AgentBacklogTab Component
 * AIDLC Administrative Backend
 *
 * Shows backlog items assigned to or reported by a specific agent.
 * Supports filtering, status progression, and creating new items
 * pre-assigned to the agent.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  getBacklogItems, createBacklogItem, updateBacklogItem,
  dispatchBacklogItem, stopBacklogItem, bulkDispatchBacklogItems, bulkStopBacklogItems,
} from '../../services/backlogService';
import { Button } from '../shared/Button';
import { Badge } from '../shared/Badge';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { BacklogItemDetail } from '../backlog/BacklogItemDetail';
import type { BacklogItem, BacklogStatus, BacklogPriority } from '../../types/backlog';

interface AgentBacklogTabProps {
  agentId: string;
  agentName: string;
}

const priorityColors: Record<string, { bg: string; text: string }> = {
  critical: { bg: 'bg-red-100', text: 'text-red-700' },
  high: { bg: 'bg-orange-100', text: 'text-orange-700' },
  medium: { bg: 'bg-yellow-100', text: 'text-yellow-700' },
  low: { bg: 'bg-gray-100', text: 'text-gray-600' },
};

const statusFlow: BacklogStatus[] = ['new', 'open', 'in_progress', 'review', 'done', 'closed'];

const statusLabels: Record<string, string> = {
  new: 'New',
  open: 'Open',
  in_progress: 'In Progress',
  review: 'Review',
  done: 'Done',
  closed: 'Closed',
};

const CARD_BASE = 'flex items-center gap-3 p-3 rounded-lg transition-all duration-200 cursor-pointer';

const statusStyles: Record<string, { card: string; badge: string }> = {
  in_progress: {
    card: `${CARD_BASE} bg-green-50/30 border-l-4 border-l-green-500 border border-green-200 hover:border-green-300 shadow-sm shadow-green-100`,
    badge: 'bg-green-100 text-green-700',
  },
  review: {
    card: `${CARD_BASE} bg-amber-50/20 border-l-4 border-l-amber-500 border border-amber-200 hover:border-amber-300`,
    badge: 'bg-amber-100 text-amber-700',
  },
  done: {
    card: `${CARD_BASE} bg-gray-50 border border-gray-100 hover:border-gray-200 hover:border-ldaja-blue/30`,
    badge: 'bg-green-100 text-green-700',
  },
  closed: {
    card: `${CARD_BASE} bg-gray-50 border border-gray-100 hover:border-gray-200 hover:border-ldaja-blue/30`,
    badge: 'bg-green-100 text-green-700',
  },
};

const DEFAULT_CARD = `${CARD_BASE} bg-gray-50 border border-gray-100 hover:border-gray-200 hover:border-ldaja-blue/30`;
const DEFAULT_BADGE = 'bg-gray-100 text-gray-600';

function nextStatus(current: string): string | null {
  const idx = statusFlow.indexOf(current as BacklogStatus);
  if (idx < 0 || idx >= statusFlow.length - 1) return null;
  return statusFlow[idx + 1];
}

export function AgentBacklogTab({ agentId, agentName }: AgentBacklogTabProps) {
  const [items, setItems] = useState<BacklogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'assigned' | 'reported' | 'all'>('assigned');
  const [showCreate, setShowCreate] = useState(false);
  const [selectedItem, setSelectedItem] = useState<BacklogItem | null>(null);

  // Create form state
  const [newTitle, setNewTitle] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newType, setNewType] = useState<'user_request' | 'defect'>('user_request');
  const [newPriority, setNewPriority] = useState<BacklogPriority>('medium');
  const [creating, setCreating] = useState(false);

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isDispatching, setIsDispatching] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const loadItems = useCallback(async () => {
    try {
      // Fetch all items and filter client-side for assigned + reported
      const all = await getBacklogItems();
      const agentItems = all.filter(i =>
        i.assignedAgent === agentId || i.reportedBy === agentName
      );
      setItems(agentItems);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [agentId, agentName]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const filteredItems = items.filter(i => {
    if (filter === 'assigned') return i.assignedAgent === agentId;
    if (filter === 'reported') return i.reportedBy === agentName;
    return true;
  });

  const assignedCount = items.filter(i => i.assignedAgent === agentId).length;
  const reportedCount = items.filter(i => i.reportedBy === agentName).length;

  const handleCreate = async () => {
    if (!newTitle.trim()) return;
    setCreating(true);
    try {
      await createBacklogItem({
        title: newTitle.trim(),
        description: newDesc.trim(),
        type: newType,
        priority: newPriority,
        assignedAgent: agentId,
        project: 'aidlc_admin',
        labels: [],
      });
      setNewTitle('');
      setNewDesc('');
      setNewType('user_request');
      setNewPriority('medium');
      setShowCreate(false);
      await loadItems();
    } catch {
      // silent
    } finally {
      setCreating(false);
    }
  };

  const handleAdvanceStatus = async (item: BacklogItem) => {
    const next = nextStatus(item.status);
    if (!next) return;
    try {
      await updateBacklogItem(item.id, { status: next as BacklogStatus });
      await loadItems();
    } catch {
      // silent
    }
  };

  const toggleSelect = (itemId: string) => {
    setSelectedIds(prev => prev.includes(itemId) ? prev.filter(id => id !== itemId) : [...prev, itemId]);
  };

  const handleDispatch = async (id: string) => {
    try {
      setActionError(null);
      await dispatchBacklogItem(id);
      await loadItems();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to dispatch');
    }
  };

  const handleStop = async (id: string) => {
    try {
      setActionError(null);
      await stopBacklogItem(id);
      await loadItems();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to stop');
    }
  };

  const handleBulkDispatch = async () => {
    if (selectedIds.length === 0) return;
    setIsDispatching(true);
    setActionError(null);
    try {
      const result = await bulkDispatchBacklogItems(selectedIds);
      if (result.failed > 0) {
        const errors = result.results.filter(r => !r.success).map(r => `${r.itemId}: ${r.error}`);
        setActionError(`${result.dispatched} dispatched, ${result.failed} failed: ${errors.join('; ')}`);
      }
      setSelectedIds([]);
      await loadItems();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Bulk dispatch failed');
    } finally {
      setIsDispatching(false);
    }
  };

  const handleBulkStop = async () => {
    if (selectedIds.length === 0) return;
    setIsStopping(true);
    setActionError(null);
    try {
      await bulkStopBacklogItems(selectedIds);
      setSelectedIds([]);
      await loadItems();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Bulk stop failed');
    } finally {
      setIsStopping(false);
    }
  };

  if (loading) return <LoadingSpinner message="Loading backlog..." size="sm" />;

  return (
    <div>
      {/* Header with filter tabs and create button */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-1">
          {([
            { key: 'assigned', label: 'Assigned', count: assignedCount },
            { key: 'reported', label: 'Reported', count: reportedCount },
            { key: 'all', label: 'All', count: items.length },
          ] as const).map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                filter === f.key
                  ? 'bg-ldaja-blue text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {f.label}
              <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] ${
                filter === f.key ? 'bg-white/20' : 'bg-gray-200'
              }`}>{f.count}</span>
            </button>
          ))}
        </div>
        <Button variant="primary" size="sm" onClick={() => setShowCreate(!showCreate)}>
          {showCreate ? 'Cancel' : '+ New Item'}
        </Button>
      </div>

      {/* Create form */}
      {showCreate && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4 space-y-3">
          <input
            type="text"
            placeholder="Title"
            value={newTitle}
            onChange={e => setNewTitle(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ldaja-blue/50"
          />
          <textarea
            placeholder="Description (optional)"
            value={newDesc}
            onChange={e => setNewDesc(e.target.value)}
            rows={2}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ldaja-blue/50 resize-none"
          />
          <div className="flex gap-3 items-center">
            <select
              value={newType}
              onChange={e => setNewType(e.target.value as 'user_request' | 'defect')}
              className="px-3 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ldaja-blue/50"
            >
              <option value="user_request">User Request</option>
              <option value="defect">Defect</option>
            </select>
            <select
              value={newPriority}
              onChange={e => setNewPriority(e.target.value as BacklogPriority)}
              className="px-3 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ldaja-blue/50"
            >
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
            <span className="text-[10px] text-gray-400">Assigned to: {agentName}</span>
            <div className="flex-1" />
            <Button variant="primary" size="sm" onClick={handleCreate} disabled={!newTitle.trim() || creating} isLoading={creating}>
              Create
            </Button>
          </div>
        </div>
      )}

      {/* Action Error */}
      {actionError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-2 mb-3">
          <p className="text-red-700 text-[10px]">{actionError}</p>
          <button onClick={() => setActionError(null)} className="text-red-600 text-[10px] underline">Dismiss</button>
        </div>
      )}

      {/* Bulk Toolbar */}
      {selectedIds.length > 0 && (
        <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-ldaja-blue/5 border border-ldaja-blue/20 rounded-lg">
          <span className="text-[10px] font-medium text-ldaja-blue">{selectedIds.length} selected</span>
          <div className="flex-1" />
          <Button variant="primary" size="sm" onClick={handleBulkDispatch} isLoading={isDispatching} disabled={isDispatching || isStopping}>
            Start Selected
          </Button>
          <Button variant="secondary" size="sm" onClick={handleBulkStop} isLoading={isStopping} disabled={isDispatching || isStopping}>
            Stop Selected
          </Button>
          <button onClick={() => setSelectedIds([])} className="text-[10px] text-gray-500 hover:text-gray-700">Clear</button>
        </div>
      )}

      {/* Items list */}
      {filteredItems.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <svg className="w-10 h-10 mx-auto mb-3 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <p className="text-sm">No backlog items {filter === 'assigned' ? 'assigned to' : filter === 'reported' ? 'reported by' : 'for'} {agentName}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredItems.map(item => {
            const pc = priorityColors[item.priority] || priorityColors.low;
            const next = nextStatus(item.status);
            const ss = statusStyles[item.status];
            const cardClasses = ss?.card || DEFAULT_CARD;
            return (
              <div
                key={item.id}
                onClick={() => setSelectedItem(item)}
                className={cardClasses}
              >
                {/* Checkbox */}
                <input
                  type="checkbox"
                  checked={selectedIds.includes(item.id)}
                  onChange={() => toggleSelect(item.id)}
                  onClick={e => e.stopPropagation()}
                  className="rounded border-gray-300 text-ldaja-blue focus:ring-ldaja-blue/50 flex-shrink-0"
                />

                {/* Priority badge */}
                <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase ${pc.bg} ${pc.text}`}>
                  {item.priority}
                </span>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-ldaja-black truncate">{item.title}</span>
                    <Badge variant={item.type === 'defect' ? 'danger' : 'info'}>
                      {item.type === 'defect' ? 'Bug' : 'Request'}
                    </Badge>
                    {item.moscow && (
                      <span className={`px-1.5 py-0.5 text-[9px] font-medium rounded ${
                        item.moscow === 'must' ? 'bg-red-100 text-red-700' :
                        item.moscow === 'should' ? 'bg-orange-100 text-orange-700' :
                        item.moscow === 'could' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-500'
                      }`}>{item.moscow === 'must' ? 'Must' : item.moscow === 'should' ? 'Should' : item.moscow === 'could' ? 'Could' : "Won't"}</span>
                    )}
                    {item.labels.map(l => (
                      <span key={l} className="px-1.5 py-0.5 bg-gray-200 text-gray-500 text-[9px] rounded">{l}</span>
                    ))}
                  </div>
                  <div className="flex items-center gap-3 mt-0.5">
                    <span className="text-[10px] text-gray-400">{item.id}</span>
                    {item.assignedAgent && item.assignedAgent !== agentId && (
                      <span className="text-[10px] text-gray-400">Assigned: {item.assignedAgent}</span>
                    )}
                    {item.reportedBy && (
                      <span className="text-[10px] text-gray-400">By: {item.reportedBy}</span>
                    )}
                    <span className="text-[10px] text-gray-400">{item.project}</span>
                    {item.comments.length > 0 && (
                      <span className="text-[10px] text-gray-400">{item.comments.length} comment{item.comments.length !== 1 ? 's' : ''}</span>
                    )}
                  </div>
                </div>

                {/* Status + advance button */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`px-2 py-0.5 rounded text-[10px] font-medium inline-flex items-center gap-1 ${ss?.badge || DEFAULT_BADGE}`}>
                    {item.status === 'in_progress' && (
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
                      </span>
                    )}
                    {statusLabels[item.status] || item.status}
                  </span>
                  {/* Dispatch for open items assigned to this agent */}
                  {item.status === 'open' && item.assignedAgent === agentId && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDispatch(item.id); }}
                      className="p-1 text-green-500 hover:text-green-700 hover:bg-green-50 rounded transition-colors"
                      title="Dispatch to agent"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                        <path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </button>
                  )}
                  {/* Stop for in_progress items */}
                  {item.status === 'in_progress' && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleStop(item.id); }}
                      className="p-1 text-orange-500 hover:text-orange-700 hover:bg-orange-50 rounded transition-colors"
                      title="Stop and revert to open"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        <path d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
                      </svg>
                    </button>
                  )}
                  {/* Advance for review/done items */}
                  {next && item.status !== 'open' && item.status !== 'in_progress' && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleAdvanceStatus(item); }}
                      className="p-1 text-gray-400 hover:text-ldaja-blue hover:bg-ldaja-blue/10 rounded transition-colors"
                      title={`Move to ${statusLabels[next]}`}
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Summary footer */}
      {items.length > 0 && (
        <div className="flex gap-4 mt-4 pt-3 border-t border-gray-100">
          <span className="text-[10px] text-gray-400">
            {items.filter(i => i.assignedAgent === agentId && (i.status === 'new' || i.status === 'open' || i.status === 'in_progress')).length} active
          </span>
          <span className="text-[10px] text-gray-400">
            {items.filter(i => i.assignedAgent === agentId && i.status === 'review').length} in review
          </span>
          <span className="text-[10px] text-gray-400">
            {items.filter(i => i.assignedAgent === agentId && (i.status === 'done' || i.status === 'closed')).length} completed
          </span>
        </div>
      )}

      {/* Detail Modal */}
      {selectedItem && (
        <BacklogItemDetail
          item={selectedItem}
          isOpen={!!selectedItem}
          onClose={() => setSelectedItem(null)}
          onUpdated={() => { loadItems(); setSelectedItem(null); }}
        />
      )}
    </div>
  );
}
